import React, {
  createContext,
  useContext,
  useState,
  useCallback,
  useMemo,
} from "react";
import { Snackbar, Alert } from "@mui/material";

// Define the shape of the snackbar state
const initialSnackbarState = {
  open: false,
  message: "",
  severity: "info", // Can be 'error', 'warning', 'info', or 'success'
  autoHideDuration: 4000,
  anchorOrigin: { vertical: "bottom", horizontal: "center" },
};

// Define the shape of the context object
// The 'showSnackbar' function is what child components will call
const SnackbarContext = createContext({
  // Default values for the consumer
  showSnackbar: (
    message: string,
    severity: "error" | "warning" | "info" | "success",
    duration: number
  ) => {},
});

// Custom hook to use the snackbar context
export const useSnackbar = () => useContext(SnackbarContext);

export const SnackbarProvider = ({
  children,
}: {
  children: React.ReactNode;
}) => {
  const [snackbar, setSnackbar] = useState(initialSnackbarState);

  // Function to show the snackbar, to be exposed via context
  // Memoized so its identity stays stable across renders — several pages
  // list `showSnackbar` as a useEffect dependency, and a new reference on
  // every snackbar call would re-trigger those effects (e.g. re-fetching
  // and wiping out unsaved local state) any time a message is shown.
  const showSnackbar = useCallback(
    (
      message: string,
      severity: "error" | "warning" | "info" | "success" = "info",
      duration: number
    ) => {
      setSnackbar({
        open: true,
        message,
        severity,
        autoHideDuration: duration || initialSnackbarState.autoHideDuration,
        anchorOrigin: initialSnackbarState.anchorOrigin, // You can make this configurable too
      });
    },
    []
  );

  const handleClose = useCallback(
    (event?: React.SyntheticEvent | Event, reason?: string) => {
      if (reason === "clickaway") {
        return;
      }
      // Only close it, but retain message/severity until it unmounts
      setSnackbar((prev) => ({ ...prev, open: false }));
    },
    []
  );

  // The context value we expose to the rest of the app
  const contextValue = useMemo(() => ({ showSnackbar }), [showSnackbar]);

  const { open, message, severity, autoHideDuration, anchorOrigin } = snackbar;

  return (
    <SnackbarContext.Provider value={contextValue}>
      {children}
      <Snackbar
        open={open}
        autoHideDuration={autoHideDuration}
        onClose={handleClose}
        anchorOrigin={{ vertical: "top", horizontal: "right" }}
      >
        <Alert
          onClose={handleClose}
          severity={severity as any}
          sx={{ width: "100%" }}
        >
          {message}
        </Alert>
      </Snackbar>
    </SnackbarContext.Provider>
  );
};
